import { T, useGT } from 'gt-react';
import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { orpc } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { taskNamespaceSelectAvailability } from './taskTypeStepConfig';

interface TaskNamespaceConfigFieldProps {
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  /** When true, the select must not mutate workflow configuration. */
  readOnly?: boolean;
}

function TaskNamespaceHelpText({
  namespacesUnavailable,
  loading,
  enforced,
}: {
  namespacesUnavailable: boolean;
  loading: boolean;
  enforced: boolean;
}) {
  if (namespacesUnavailable) {
    return (
      <T context="namespaces cannot be loaded">
        Namespaces are unavailable right now. The saved selection is kept.
      </T>
    );
  }
  if (loading) {
    return <T context="loading namespaces">Loading namespaces...</T>;
  }
  if (enforced) {
    return (
      <T context="help text when task type enforces its namespace">
        The selected task type places tasks in this namespace.
      </T>
    );
  }
  return (
    <T context="help text for workflow task namespace field">
      The task is created in this namespace. Defaults to the organization's
      default namespace.
    </T>
  );
}

/** Sentinel select value for "use the org default namespace". */
const DEFAULT_VALUE = '__default__';

/**
 * Config field for choosing the namespace a task step creates its task in.
 * Stores the namespace `key` in config as `taskNamespaceKey` (portable
 * across orgs; the handler resolves it to an id at run time). Selecting the
 * default removes the key so the task falls into the org default namespace.
 */
export function TaskNamespaceConfigField({
  config,
  onConfigChange,
  readOnly = false,
}: TaskNamespaceConfigFieldProps) {
  const gt = useGT();
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const {
    data: namespaces,
    isLoading,
    isError,
  } = useQuery({
    ...orpc.namespaces.list.queryOptions({ input: {} }),
    enabled: canReadTasks,
    staleTime: 5 * 60 * 1000,
  });
  const {
    data: taskTypes,
    isLoading: taskTypesLoading,
    isError: taskTypesError,
  } = useQuery({
    ...orpc.taskTypes.list.queryOptions({ input: {} }),
    enabled: canReadTasks,
    staleTime: 5 * 60 * 1000,
  });

  const selectedType =
    typeof config.taskTypeKey === 'string'
      ? (taskTypes ?? []).find(
          (taskType) => taskType.key === config.taskTypeKey,
        )
      : undefined;
  const enforcedNamespace = selectedType?.taskNamespace ?? null;

  const selectedKey = enforcedNamespace
    ? enforcedNamespace.key
    : typeof config.taskNamespaceKey === 'string'
      ? config.taskNamespaceKey
      : DEFAULT_VALUE;
  const namespaceOptions = namespaces ?? [];
  const { namespacesUnavailable, loading } = taskNamespaceSelectAvailability({
    canReadTasks,
    namespacesError: isError,
    namespacesLoading: isLoading,
    taskTypesError: taskTypesError,
    taskTypesLoading,
  });
  const selectedKeyInOptions =
    selectedKey === DEFAULT_VALUE ||
    namespaceOptions.some((namespace) => namespace.key === selectedKey);

  function handleChange(nextValue: string) {
    const { taskNamespaceKey: _key, taskNamespaceId: _id, ...rest } = config;
    onConfigChange(
      nextValue === DEFAULT_VALUE
        ? rest
        : { ...rest, taskNamespaceKey: nextValue },
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="taskNamespaceKey" className="text-sm font-medium">
        <T context="workflow task node, namespace field label">Namespace</T>
      </Label>
      <Select
        value={selectedKey}
        onValueChange={handleChange}
        disabled={
          namespacesUnavailable ||
          loading ||
          readOnly ||
          enforcedNamespace != null
        }
      >
        <SelectTrigger id="taskNamespaceKey" className="w-full">
          <SelectValue
            placeholder={gt('Organization default', {
              context: 'placeholder for task namespace select',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_VALUE}>
            <T context="task namespace default option">Organization default</T>
          </SelectItem>
          {/* Keep a saved key visible (and its value intact) even when it is
              not in the loaded list — e.g. list unavailable or key archived. */}
          {!selectedKeyInOptions && (
            <SelectItem value={selectedKey}>{selectedKey}</SelectItem>
          )}
          {namespaceOptions.map((namespace) => (
            <SelectItem key={namespace.id} value={namespace.key}>
              {namespace.name} ({namespace.key})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        <TaskNamespaceHelpText
          namespacesUnavailable={namespacesUnavailable}
          loading={loading}
          enforced={enforcedNamespace != null}
        />
      </p>
    </div>
  );
}
